import React, { useRef, useEffect, useCallback } from 'react'

import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import {
  selectCurrentFileData,
  selectIsFileDataLoading,
  selectCurrentCommentData,
  selectCurrentReplyCommentData,
  selectCurrentActiveNotification,
  selectNotificationPreviewError
} from 'features/common/model/selectors/notificationsSelectors'
import { getMiddlePreviewLink } from 'shared/utils/filesUtils'
import moment from 'moment'
import { openFilesPreviewThunk } from 'features/files-preview/model/slice/filesPreviewSlice'
import { markNotificationAsRead, setIsNotificationsPopoverOpened } from 'features/common/model/slice/notificationsSlice'
import { userIdSelector } from 'features/user/model/selectors/userSelectors'
import { AI_BOT_USER_ID } from 'shared/constants/ai'
import { marked } from 'marked'
import { useNavigate } from 'react-router-dom'
import { Avatar } from 'antd'

import { familyMembersWithAvatarsSelector } from '../../../team/model/selectors/teamSelectors'

import { PreviewSkeleton } from './NotificationPreviewSkeleton'

interface NotificationPreviewProps {
  previewPosition: {
    top?: number;
    bottom?: number;
    right: number;
    bottomAlign: boolean;
    initialRender: boolean;
  }
  onPreviewMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void
  onMeasure?: (height: number) => void
}

export const NotificationPreview: React.FC<NotificationPreviewProps> = ({
  previewPosition,
  onPreviewMouseLeave,
  onMeasure
}) => {
  const dispatch = useAppDispatch()
  const previewContainerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const currentFileData = useAppSelector(selectCurrentFileData)
  const currentCommentData = useAppSelector(selectCurrentCommentData)
  const currentReplyCommentData = useAppSelector(selectCurrentReplyCommentData)
  const isFileDataLoading = useAppSelector(selectIsFileDataLoading)
  const currentActiveNotification = useAppSelector(selectCurrentActiveNotification)
  const previewError = useAppSelector(selectNotificationPreviewError)
  const userId = useAppSelector(userIdSelector)
  const familyMembers = useAppSelector(familyMembersWithAvatarsSelector)
  const familyMembersRef = useRef<typeof familyMembers>(null)

  useEffect(() => {
    familyMembersRef.current = familyMembers
  }, [familyMembers])

  const getMemberAvatarUrl = useCallback((memberId) => {
    const member = familyMembersRef.current?.find((member) => member.user_id === memberId)
    return member?.avatarUrl
  }, [])

  const positionStyle = React.useMemo(() => {
    if (previewPosition.bottomAlign) {
      return {
        bottom: previewPosition.bottom,
        right: previewPosition.right
      }
    } else {
      return {
        top: previewPosition.top,
        right: previewPosition.right
      }
    }
  }, [previewPosition])

  useEffect(() => {
    if (previewContainerRef.current && onMeasure && previewPosition.initialRender) {
      const containerHeight = previewContainerRef.current.getBoundingClientRect().height
      onMeasure(containerHeight)
    }
  }, [isFileDataLoading, previewPosition.initialRender, onMeasure, currentFileData, currentCommentData, currentReplyCommentData])

  useEffect(() => {
    if (previewContainerRef.current && !previewPosition.bottomAlign) {
      const container = previewContainerRef.current
      const containerRect = container.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      if (containerRect.bottom > viewportHeight) {
        const newTop = Math.max(10, viewportHeight - containerRect.height)
        container.style.top = `${newTop}px`
      }
    }
  }, [isFileDataLoading, currentFileData, currentCommentData, currentReplyCommentData, previewPosition.bottomAlign])

  const handleOpenFile = () => {
    dispatch(setIsNotificationsPopoverOpened(false))

    if (!currentActiveNotification.is_viewed) {
      dispatch(markNotificationAsRead({ userId, notificationId: currentActiveNotification.id }))
    }

    dispatch(openFilesPreviewThunk({
      items: [currentFileData],
      currentItemId:
      currentFileData.id,
      totalItemsCount: 1,
      commentIdForScrollTo: currentReplyCommentData?.id || currentCommentData?.id,
      activeTab: 'comments',
      navigate
    }))
  }

  return (
    <SPreviewOuterContainer
      ref={previewContainerRef}
      style={positionStyle}
      onMouseLeave={onPreviewMouseLeave}
      className="preview-container"
    >
      <SPreviewContainer>
        {!!isFileDataLoading && <PreviewSkeleton />}

        {!isFileDataLoading && !!currentFileData && (
          <>
            <SFilePreviewImgContainer>
              <SFilePreviewImg src={getMiddlePreviewLink(currentFileData)} />

              <SOpenFileBtn onClick={handleOpenFile}>
                <SOpenFileBtnIcon src="/icons/open.svg" />
              </SOpenFileBtn>
            </SFilePreviewImgContainer>

            {!!currentReplyCommentData && (
              <SComment onClick={handleOpenFile}>
                <SAvatar
                  $hasAvatar={!!getMemberAvatarUrl(currentReplyCommentData.author_id)}
                  src={getMemberAvatarUrl(currentReplyCommentData.author_id) || undefined}
                >
                  {currentReplyCommentData.author_name[0]}
                </SAvatar>

                <SCommentContent>
                  <SCommentAuthorName>
                    {currentReplyCommentData.author_name}

                    <SCommentTime>
                      {moment(currentReplyCommentData.created).fromNow()}
                    </SCommentTime>
                  </SCommentAuthorName>

                  <SCommentText>
                    {currentReplyCommentData.message}
                  </SCommentText>
                </SCommentContent>
              </SComment>
            )}

            {!!currentCommentData && (
              <SComment onClick={handleOpenFile}>
                <SAvatar
                  $hasAvatar={!!getMemberAvatarUrl(currentCommentData.author_id)}
                  src={getMemberAvatarUrl(currentCommentData.author_id) || undefined}
                >
                  {currentCommentData.author_name[0]}
                </SAvatar>

                <SCommentContent>
                  <SCommentAuthorName>
                    {currentCommentData.author_name}

                    <SCommentTime>
                      {moment(currentCommentData.created).fromNow()}
                    </SCommentTime>
                  </SCommentAuthorName>

                  {
                    currentCommentData.author_id === AI_BOT_USER_ID && (
                      <SCommentText dangerouslySetInnerHTML={{ __html: marked(currentCommentData.message, { async: false }) as string }} />
                    )
                  }

                  {
                    currentCommentData.author_id !== AI_BOT_USER_ID && (
                      <SCommentText>
                        {currentCommentData.message}
                      </SCommentText>
                    )
                  }
                </SCommentContent>
              </SComment>
            )}
          </>
        )}

        {!isFileDataLoading && !!previewError && (
          <SErrorState>
            {previewError}
          </SErrorState>
        )}
      </SPreviewContainer>
    </SPreviewOuterContainer>
  )
}

const SPreviewOuterContainer = styled.div`
  position: fixed;
  right: 0;
  left: auto;
  z-index: 10;
  padding-right: 8px;
  transform-origin: right top;
`

const SPreviewContainer = styled.div`
  width: 468px;
  height: fit-content;
  min-height: 320px;
  box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  background-color: #fff;
  padding: 24px;
  max-height: 80vh;
  overflow-y: auto;
`

const SFilePreviewImgContainer = styled.div`
  width: 100%;
  height: 236px;
  position: relative;
`

const SFilePreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const SOpenFileBtn = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  background-color: #FFFFFF;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #F5F5F5;
  }
`

const SOpenFileBtnIcon = styled.img`
  width: 16px;
  height: 16px;
`

const SComment = styled.div`
  margin-top: 36px;
  width: 100%;
  display: flex;
  cursor: pointer;
`

const SAvatar = styled(Avatar)<{ $hasAvatar?: boolean }>`
  width: 32px;
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.$hasAvatar ? 'transparent' : '#00000040'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
`

const SCommentContent = styled.div`
  margin-left: 16px;
`

const SCommentAuthorName = styled.div`
  font-size: 14px;
  font-weight: 600;
`

const SCommentText = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 400;
  color: #000000E0;
  line-height: 20px;
  word-break: break-all;
`

const SCommentTime = styled.span`
  margin-left: 5px;
  color: #00000073;
  font-weight: 400;
`

const SErrorState = styled.div`
  padding: 24px;
  text-align: center;
  color: #00000073;
  font-size: 14px;
`
